import { useEffect, useMemo, useState } from "react";
import { Radio, RadioGroup, Tooltip, TooltipTrigger } from "@artis/ui";
import { ModalSheet } from "../components/ModalSheet";
import { Button } from "../ui";
import { useAllProductsOptions, useGlobalisedPackageIds, useIsUmiInternalAdmin } from "./hooks";
import {
  GLOBALISED_EDIT_CURVE_WARNING,
  GLOBALISED_NON_MANAGER_EDIT_MENU_TOOLTIP,
  isPackageGlobalised,
} from "./globalisedFolders";
import { useAutocomplete } from "../components/Autocomplete/hooks";
import { Autocomplete } from "../components/Autocomplete/Autocomplete";
import { FilterToggleButton } from "../components/FilterToggleButton";
import { ModalActionsRow } from "../components/ModalActionsRow";
import { CurveFilter, CurveFilterPreview, CurveFilterProvider, type TCurveFilter } from "./CurveFilter";

export type SelectCurveFor = "copy" | "edit";

// Copy only supports live/delta/all (Snapshot curves can't be copied).
// Edit supports all curve types, including Snapshot (metadata-only editing).
const COPY_FILTER_OPTIONS = ["live", "delta", "all"] as const;
const EDIT_FILTER_OPTIONS = ["live", "delta", "snapshot", "all"] as const;

export const SNAPSHOT_HELP_TEXT = "NB: only the metadata of a Snapshot Curve can be viewed and edited";

export const curveTypeFilterLabel: Record<string, string> = {
  all: "All",
  live: "Live",
  snapshot: "Snapshot",
  delta: "Delta",
};

type CopyFilter = (typeof COPY_FILTER_OPTIONS)[number];
type EditFilter = (typeof EDIT_FILTER_OPTIONS)[number];

/**
 * Single Curve picker behind both entry points for copying/editing a Curve —
 * the Market page nav menu and Curve Management itself (WEB-2017) — so the
 * title, filters and spacing stay identical.
 */
export function SelectCurveModal({
  mode,
  onConfirm,
  onCancel,
  onSelectionChange,
  confirmDisabled = false,
}: {
  mode: SelectCurveFor;
  onConfirm: (curveId: string) => void;
  onCancel: () => void;
  /** Fires whenever the picked Curve changes, for callers that need to preload it. */
  onSelectionChange?: (curveId: string | undefined) => void;
  /** Blocks confirm on top of "nothing selected", e.g. while the Curve loads. */
  confirmDisabled?: boolean;
}) {
  const title = mode === "copy" ? "Create Curve from copy" : "Edit an existing Curve";
  const confirmLabel = mode === "copy" ? "Copy Curve" : "Edit Curve";

  const [copyFilter, setCopyFilter] = useState<CopyFilter>("live");
  const [editFilter, setEditFilter] = useState<EditFilter>("live");
  const [filters, setFilters] = useState<TCurveFilter>({ types: [], regions: [], commodities: [] });
  // Same viewport-bounded height rule as other Curve dropdowns (e.g. Add Curves to Page).
  const [listHeight, setListHeight] = useState(200);

  useEffect(() => {
    const calculateListHeight = () => {
      const modalOverhead = 450;
      const bottomMargin = 20;
      const availableHeight = window.innerHeight - modalOverhead - bottomMargin;
      const boundedHeight = Math.max(150, Math.min(availableHeight, 400));
      setListHeight(boundedHeight);
    };

    calculateListHeight();
    window.addEventListener("resize", calculateListHeight);
    return () => window.removeEventListener("resize", calculateListHeight);
  }, []);

  const activeFilter = mode === "copy" ? copyFilter : editFilter;
  const filterOptions = mode === "copy" ? COPY_FILTER_OPTIONS : EDIT_FILTER_OPTIONS;

  // Fetch unfiltered (by curve source) so we can independently determine
  // which of the Live/Delta/Snapshot/All radio options actually have any
  // curves available, and disable the ones that don't (see WEB-1888 — users
  // could otherwise select e.g. "Snapshot" with nothing to pick from).
  const { data: productOptionsData } = useAllProductsOptions();
  const { types, regions, commodities } = filters;
  const isUmiInternalAdmin = useIsUmiInternalAdmin();
  const globalisedIds = useGlobalisedPackageIds();

  // Users can only copy/edit customer Curves in Folders they have Write permission to.
  const writableCustomerCurves = useMemo(
    () =>
      productOptionsData.curves.filter((curve) => {
        const artisType = curve.metadata?.artis_type;
        const hasWriteAccess = curve.metadata?.packageByPackage?.permissions?.[0]?.permission === "write";
        return artisType === "customer_curve" && hasWriteAccess;
      }),
    [productOptionsData.curves],
  );

  const filterOptionHasCurves = useMemo(() => {
    const map: Record<string, boolean> = {};
    for (const key of filterOptions) {
      map[key] =
        key === "all"
          ? writableCustomerCurves.length > 0
          : writableCustomerCurves.some((c) => c.metadata?.curve_type === key);
    }
    return map;
  }, [writableCustomerCurves, filterOptions]);

  const productOptionsTypes = productOptionsData.types;
  const productOptionsRegions = productOptionsData.regions;
  const productOptionsCommodities = productOptionsData.commodities;

  const isFiltered = useMemo(() => {
    if (types.length === 0 && regions.length === 0 && commodities.length === 0) return false;
    return (
      types.length !== productOptionsTypes.length ||
      regions.length !== productOptionsRegions.length ||
      commodities.length !== productOptionsCommodities.length
    );
  }, [types, regions, commodities, productOptionsTypes, productOptionsRegions, productOptionsCommodities]);

  const filteredProducts = useMemo(() => {
    const matching = writableCustomerCurves.filter((curve) => {
      if (activeFilter !== "all" && curve.metadata?.curve_type !== activeFilter) return false;

      const type = curve.metadata?.product_type;
      const region = curve.metadata?.geographicalRegionByGeographicalRegion?.geographical_region;
      const commodity = curve.metadata?.commodityGroupByCommodityGroup?.commodity_group;
      return (
        (types.length === 0 || !type || types.includes(type)) &&
        (regions.length === 0 || !region || regions.includes(region)) &&
        (commodities.length === 0 || !commodity || commodities.includes(commodity))
      );
    });

    // Copying is unrestricted — it produces a new Curve — but only managers may
    // edit a Curve that sits in a globalised Folder (WEB-1953).
    if (mode !== "edit" || isUmiInternalAdmin) return matching;

    return matching.map((curve) =>
      isPackageGlobalised(curve.metadata?.package, globalisedIds)
        ? { ...curve, disabled: true, disabledTooltip: GLOBALISED_NON_MANAGER_EDIT_MENU_TOOLTIP }
        : curve,
    );
  }, [types, regions, commodities, writableCustomerCurves, activeFilter, mode, isUmiInternalAdmin, globalisedIds]);

  const autocompleteProps = useAutocomplete({ multiple: false });
  const [selectedProduct] = autocompleteProps.singleState;
  const selectedProductId = selectedProduct?.value;

  useEffect(() => {
    onSelectionChange?.(selectedProductId);
  }, [selectedProductId, onSelectionChange]);

  const selectedCurveIsGlobalised = useMemo(() => {
    if (mode !== "edit" || !selectedProductId) return false;
    const curve = writableCustomerCurves.find((c) => c.value === selectedProductId);
    return isPackageGlobalised(curve?.metadata?.package, globalisedIds);
  }, [mode, selectedProductId, writableCustomerCurves, globalisedIds]);

  return (
    <ModalSheet
      title={title}
      onClose={onCancel}
      showHeaderCloseIcon
      closeButtonVariant="secondary"
      hideCloseButton
      disableOutsideClick
      className="max-w-[640px] px-[var(--padding-medium-500,15px)] py-[16px]"
    >
      <div className="flex flex-col gap-[var(--padding-medium-500,15px)] w-full mt-[16px]">
        <RadioGroup>
          {filterOptions.map((key) => {
            const disabled = !filterOptionHasCurves[key];
            return (
              <TooltipTrigger key={key} placement="top">
                <Radio
                  label={curveTypeFilterLabel[key]}
                  checked={activeFilter === key}
                  disabled={disabled}
                  onChange={() => {
                    if (mode === "copy") setCopyFilter(key as CopyFilter);
                    else setEditFilter(key as EditFilter);
                  }}
                />
                <Tooltip>
                  {disabled ? `No ${mode === "copy" ? "copyable" : "editable"} Curves match this filter` : undefined}
                </Tooltip>
              </TooltipTrigger>
            );
          })}
        </RadioGroup>

        <div className="flex flex-col gap-[var(--padding-small-100,3px)]">
          <p className="text-foreground text-[12px] leading-[18px]">Select Curve</p>
          <CurveFilterProvider>
            <div className="flex flex-col gap-2">
              <div className="flex gap-[var(--spacing-scale-small-200,6px)] items-center">
                <div className="flex-1 min-w-0">
                  <Autocomplete
                    multiple={false}
                    {...autocompleteProps}
                    autoComplete={true}
                    autoFocus={true}
                    options={filteredProducts}
                    listHeight={listHeight}
                    placeholder="Search Curve..."
                  />
                </div>
                {productOptionsData && (
                  <CurveFilter products={productOptionsData} filters={filters} setFilters={setFilters}>
                    <FilterToggleButton isActive={isFiltered} />
                  </CurveFilter>
                )}
              </div>
              <CurveFilterPreview filters={filters} setFilters={setFilters} />
            </div>
          </CurveFilterProvider>
          {selectedCurveIsGlobalised && (
            <output className="block text-[12px] leading-[18px] text-danger">{GLOBALISED_EDIT_CURVE_WARNING}</output>
          )}
          {mode === "edit" && editFilter === "snapshot" && (
            <p className="text-[12px] leading-[18px] text-foreground-muted">{SNAPSHOT_HELP_TEXT}</p>
          )}
        </div>

        <ModalActionsRow>
          <Button buttonType="cancel" onClick={onCancel}>
            Cancel
          </Button>
          <Button
            disabled={!selectedProductId || confirmDisabled}
            onClick={() => {
              if (selectedProductId) onConfirm(selectedProductId);
            }}
          >
            {confirmLabel}
          </Button>
        </ModalActionsRow>
      </div>
    </ModalSheet>
  );
}
